import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, MapPin, Clock, Users, Building2 } from 'lucide-react';
import OptimizedImage from '@/components/OptimizedImage';
import GrainTexture from '@/components/GrainTexture';
import GeoPattern from '@/components/GeoPattern';
import organisatieVisual from '@/assets/systemique/organisatie-visual.webp';
import { SEO } from '@/components/SEO';
import { CONTACT, DEFAULT_OG_IMAGE, SITE_ORIGIN, SITE_NAME } from '@/seo/siteConfig';

const CANONICAL = `${SITE_ORIGIN}/organisatieopstellingen-noord-holland`;

const werkgebiedSteden = [
  'Amsterdam',
  'Haarlem',
  'Alkmaar',
  'Zaandam',
  'Hilversum',
];

const wanneerGeschikt = [
  'Hoog verloop of veel uitval in een team',
  'Terugkerende conflicten of stroeve samenwerking',
  'Vastgelopen verandertraject of reorganisatie',
  'Leiderschapsvragen of onduidelijke rollen en plekken',
  'Na fusie, overname of nieuwe samenwerking',
];

const faqItems = [
  {
    id: 'hoe',
    question: 'Hoe werkt een organisatieopstelling?',
    answer:
      'Een opstelling maakt de onzichtbare dynamiek in een team of organisatie zichtbaar. Deelnemers of objecten representeren sleutelfiguren of afdelingen. Door hun positie en onderlinge afstand te verkennen, komen patronen aan het licht die in gesprekken verborgen blijven.',
  },
  {
    id: 'locatie',
    question: 'Waar vinden de sessies plaats?',
    answer:
      'De praktijk is in Castricum, op 20 minuten van Amsterdam. Sessies kunnen ook op jullie kantoor of vergaderlocatie plaatsvinden, waar dan ook in Nederland.',
  },
  {
    id: 'duur',
    question: 'Hoelang duurt een sessie?',
    answer:
      'Reken op 1,5 tot 2 uur per casus. Voor een teamsessie plannen we meestal een dagdeel of een hele dag, afhankelijk van het vraagstuk.',
  },
  {
    id: 'verschil',
    question: 'Wat is het verschil met teamcoaching?',
    answer:
      'Teamcoaching richt zich op gedrag en communicatie. Een organisatieopstelling werkt in de diepere laag: plek, ordening en balans in het systeem. Beide vormen vullen elkaar goed aan.',
  },
];

const OrganisatieopstellingenNoordHolland = () => (
  <>
    <SEO
      title="Organisatieopstellingen Noord-Holland | Castricum, Amsterdam & Haarlem"
      description="Organisatieopstellingen in Noord-Holland. Marthe van der Molen begeleidt teams en organisaties vanuit Castricum, Amsterdam, Haarlem en omgeving. Op locatie of in-house."
      keywords="organisatieopstellingen Noord-Holland, organisatieopstellingen Castricum, organisatieopstellingen Amsterdam, organisatieopstellingen Haarlem, organisatieopstellingen Alkmaar, organisatieopstellingen Zaandam, systemisch werk Noord-Holland, bedrijfsopstellingen Noord-Holland"
      canonical={CANONICAL}
      ogDescription="Organisatieopstellingen in Noord-Holland. Vanuit Castricum voor teams in Amsterdam, Haarlem, Alkmaar en omgeving. Op locatie of in-house."
      ogImage={DEFAULT_OG_IMAGE}
    />

    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'Organisatieopstellingen Noord-Holland',
          serviceType: 'Organisatieopstellingen',
          provider: {
            '@type': 'Person',
            name: 'Marthe van der Molen',
            url: `${SITE_ORIGIN}/over`,
            email: CONTACT.email,
            telephone: CONTACT.phone,
          },
          areaServed: [
            { '@type': 'State', name: 'Noord-Holland' },
            { '@type': 'City', name: 'Castricum' },
            { '@type': 'City', name: 'Amsterdam' },
            { '@type': 'City', name: 'Haarlem' },
            { '@type': 'City', name: 'Alkmaar' },
            { '@type': 'City', name: 'Zaandam' },
          ],
          description:
            'Organisatieopstellingen in Noord-Holland voor teams en organisaties. Vanuit Castricum, op locatie in Amsterdam, Haarlem en omgeving, of in-house in heel Nederland.',
          url: CANONICAL,
        }),
      }}
    />

    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqItems.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          })),
        }),
      }}
    />

    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: `${SITE_ORIGIN}/`,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Organisatieopstellingen Noord-Holland',
              item: CANONICAL,
            },
          ],
        }),
      }}
    />

    <div className="animate-fade-in">
      <section className="relative min-h-[45vh] md:min-h-[55vh] flex items-center justify-center px-6 overflow-hidden bg-[#854C73]">
        <div className="absolute inset-0 z-0">
          <GrainTexture opacity={0.2} />
        </div>
        <div className="container mx-auto relative z-10 text-center max-w-4xl">
          <Button
            variant="ghost"
            asChild
            className="mb-8 hover:bg-white/20 text-white hover:text-white border border-white/30 mx-auto flex w-fit px-6 py-2 h-auto"
          >
            <Link to="/organisatieopstellingen">
              <ArrowLeft className="mr-2 w-4 h-4" /> Terug naar organisatieopstellingen
            </Link>
          </Button>
          <h1 className="font-serif text-3xl md:text-5xl text-[#F9EBEF] mb-6 leading-tight">
            Organisatieopstellingen in <span className="italic font-light font-serif">Noord-Holland</span>
          </h1>
          <p className="text-lg md:text-xl text-[#F9EBEF]/90 font-light leading-relaxed max-w-3xl mx-auto">
            Marthe van der Molen begeleidt organisatieopstellingen vanuit Castricum, op locatie bij jullie
            of in-house. Voor teams en organisaties in heel Noord-Holland en daarbuiten.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16 px-6 bg-white">
        <div className="container mx-auto max-w-3xl text-center">
          <p className="text-[#6b4912] text-lg leading-relaxed font-light">
            Een organisatieopstelling maakt zichtbaar wat in teams vaak onbespreekbaar blijft: patronen,
            verhoudingen en blokkades in de onderstroom. Marthe werkt vanuit Castricum en komt desgewenst
            naar jullie toe.
          </p>
          <p className="text-[#6b4912] text-lg leading-relaxed font-light mt-6">
            Meer weten over de werkwijze? Lees de{' '}
            <Link
              to="/organisatieopstellingen"
              className="text-[#854C73] underline underline-offset-4 hover:text-[#590D0D]"
            >
              uitleg over organisatieopstellingen
            </Link>
            .
          </p>
        </div>
      </section>

      <section className="py-12 md:py-20 px-6 bg-[#FAEBEE] relative">
        <GrainTexture opacity={0.1} />
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-white text-[#854C73] border-0">Werkgebied</Badge>
              <h2 className="font-serif text-3xl md:text-4xl text-[#854C73] mb-6">
                Werkgebied
              </h2>
              <p className="text-[#6b4912] text-lg leading-relaxed font-light mb-6">
                De praktijk is in Castricum. Sessies op locatie zijn mogelijk in heel Noord-Holland, onder meer in:
              </p>
              <ul className="grid grid-cols-2 gap-3 mb-8">
                {werkgebiedSteden.map((stad) => (
                  <li key={stad} className="flex items-center gap-2 text-[#6b4912]">
                    <MapPin className="w-4 h-4 text-[#854C73] shrink-0" />
                    {stad}
                  </li>
                ))}
              </ul>
              <p className="text-[#6b4912] leading-relaxed font-light">
                Andere locatie? Geen probleem. In-house sessies zijn door heel Nederland mogelijk.
              </p>
            </div>
            <div className="relative">
              <OptimizedImage
                src={organisatieVisual}
                webpSrc={organisatieVisual}
                alt="Organisatieopstelling sessie"
                className="rounded-2xl shadow-lg w-full h-auto"
                width={1067}
                height={1600}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl text-[#854C73] mb-6">
                Wanneer is een organisatieopstelling passend?
              </h2>
              <ul className="space-y-4">
                {wanneerGeschikt.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-[#6b4912] leading-relaxed">
                    <span className="mt-2 w-2 h-2 rounded-full bg-[#854C73] shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="font-serif text-3xl md:text-4xl text-[#854C73] mb-6">Praktisch</h2>
              <dl className="space-y-6">
                <div className="flex gap-4">
                  <Clock className="w-6 h-6 text-[#854C73] shrink-0 mt-1" />
                  <div>
                    <dt className="font-semibold text-[#854C73] mb-1">Duur</dt>
                    <dd className="text-[#6b4912] font-light">
                      1,5 tot 2 uur per casus. Teamsessies: dagdeel tot hele dag.
                    </dd>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Building2 className="w-6 h-6 text-[#854C73] shrink-0 mt-1" />
                  <div>
                    <dt className="font-semibold text-[#854C73] mb-1">Locatie</dt>
                    <dd className="text-[#6b4912] font-light">
                      Castricum, op locatie in Noord-Holland of in-house bij jullie op kantoor.
                    </dd>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Users className="w-6 h-6 text-[#854C73] shrink-0 mt-1" />
                  <div>
                    <dt className="font-semibold text-[#854C73] mb-1">Voor wie</dt>
                    <dd className="text-[#6b4912] font-light">
                      Leiders, managers, HR-professionals, bestuurders en teams met een organisatie- of
                      samenwerkingsvraag.
                    </dd>
                  </div>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 px-6 bg-[#FAEBEE] relative">
        <GrainTexture opacity={0.1} />
        <div className="container mx-auto max-w-4xl relative z-10">
          <h2 className="font-serif text-3xl md:text-4xl text-[#854C73] mb-8 text-center">
            Veelgestelde vragen
          </h2>
          <Card className="border-0 shadow-xl bg-white">
            <CardContent className="p-6 md:p-8">
              <Accordion type="single" collapsible className="w-full">
                {faqItems.map((item) => (
                  <AccordionItem key={item.id} value={item.id}>
                    <AccordionTrigger className="text-left text-lg font-semibold text-[#854C73]">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#6b4912] pt-4 leading-relaxed">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="py-12 md:py-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-[#854C73] rounded-[2rem] p-10 md:p-16 text-center text-white relative overflow-hidden">
            <GeoPattern />
            <div className="relative z-10">
              <h2 className="font-serif text-3xl md:text-4xl mb-6">
                Vrijblijvend kennismaken?
              </h2>
              <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto font-light leading-relaxed">
                Bel of mail voor een kort gesprek. We bespreken je vraag en of een organisatieopstelling
                past bij jullie situatie.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="white" asChild size="lg">
                  <Link to="/contact?onderwerp=organisatieopstelling">Neem contact op</Link>
                </Button>
                <Button variant="white" asChild size="lg">
                  <Link to="/agenda">Bekijk de agenda</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </>
);

export default OrganisatieopstellingenNoordHolland;
